<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard headline-id="identifiers" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('identifiers')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('identifiers')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('identifiers')"
                    @enter-edit-mode="onEnterEditMode('identifiers', $t('identifiers'))"
                    @leave-edit-mode="leaveEditMode('identifiers')"
                    @discard-changes="discardChanges('identifiers')"
                    @save="saveForm('identifiers')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('identifiers') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesIdentifiersForm
                        v-if="showForm('identifiers')"
                        ref="seriesIdentifiersFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <SeriesIdentifiersDisplay v-else :series-id="seriesId" />
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="typeDefinitions" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('typeDefinitions')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('typeDefinitions')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('typeDefinitions')"
                    @enter-edit-mode="onEnterEditMode('typeDefinitions', $t('type-definition'))"
                    @leave-edit-mode="leaveEditMode('typeDefinitions')"
                    @discard-changes="discardChanges('typeDefinitions')"
                    @save="saveForm('typeDefinitions')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('type-definition') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesTypeDefinitionForm
                        v-if="showForm('typeDefinitions')"
                        ref="seriesTypeDefinitionFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <SeriesTypeDefinitionDisplay v-else :series-id="seriesId" />
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="eventPartners" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('eventPartners')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('eventPartners')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('eventPartners')"
                    @enter-edit-mode="onEnterEditMode('eventPartners', $t('event-partners'))"
                    @leave-edit-mode="leaveEditMode('eventPartners')"
                    @discard-changes="discardChanges('eventPartners')"
                    @save="saveForm('eventPartners')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('event-partners') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSeriesPartnersForm
                        v-if="showForm('eventPartners')"
                        ref="seriesPartnersFormRef"
                        class="p-5"
                        :series-id="seriesId"
                    />
                    <SeriesPartnersDisplay v-else :series-id="seriesId" />
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import SeriesIdentifiersDisplay from '@/event/displays/series/SeriesIdentifiersDisplay.vue';
import SeriesPartnersDisplay from '@/event/displays/series/SeriesPartnersDisplay.vue';
import SeriesTypeDefinitionDisplay from '@/event/displays/series/SeriesTypeDefinitionDisplay.vue';
import EventSeriesIdentifiersForm from '@/event/forms/series/EventSeriesIdentifiersForm.vue';
import EventSeriesPartnersForm from '@/event/forms/series/EventSeriesPartnersForm.vue';
import EventSeriesTypeDefinitionForm from '@/event/forms/series/EventSeriesTypeDefinitionForm.vue';
import { FormRef, useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { useFluent } from 'fluent-vue';
import { computed, onUnmounted, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);
const fluent = useFluent();

const { series } = useEventSeriesInjection();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    getCardStatus,
} = useActiveCard();

const seriesIdentifiersFormRef = useTemplateRef<FormRef>('seriesIdentifiersFormRef');
const typeDefinitionFormRef = useTemplateRef<FormRef>('seriesTypeDefinitionFormRef');
const seriesPartnersFormRef = useTemplateRef<FormRef>('seriesPartnersFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('identifiers', seriesIdentifiersFormRef);
register('typeDefinitions', typeDefinitionFormRef);
register('eventPartners', seriesPartnersFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
onUnmounted(() => resetActiveCard());
</script>
